// Database types for Company Offerings (professional services)

import type { FieldValue } from "@/components/shared/UniversalCRMTable/types/base-types";

/** Fixed list of offering roles - used for staff line items */
export const OFFERING_ROLE_IDS = [
  "senior_engineer",
  "engineer",
  "junior_engineer",
  "project_manager",
  "designer",
  "qa",
  "devops",
] as const;

export type OfferingRoleId = (typeof OFFERING_ROLE_IDS)[number];

/** Role definition with label and default hourly rate */
export interface OfferingRoleDefinition {
  id: OfferingRoleId;
  label: string;
  defaultRate: number;
}

/** Canonical list of offering roles with default rates */
export const OFFERING_ROLES: OfferingRoleDefinition[] = [
  { id: "senior_engineer", label: "Senior Engineer", defaultRate: 120 },
  { id: "engineer", label: "Engineer", defaultRate: 90 },
  { id: "junior_engineer", label: "Junior Engineer", defaultRate: 55 },
  { id: "project_manager", label: "Project Manager", defaultRate: 120 },
  { id: "designer", label: "Designer", defaultRate: 90 },
  { id: "qa", label: "QA", defaultRate: 55 },
  { id: "devops", label: "DevOps", defaultRate: 90 },
];

/** Fixed list of hard cost categories */
export const HARD_COST_CATEGORY_IDS = [
  "license",
  "hosting",
  "third_party",
  "travel",
  "equipment",
  "other",
] as const;

export type HardCostCategoryId = (typeof HARD_COST_CATEGORY_IDS)[number];

/** Hard cost category definition */
export interface HardCostCategoryDefinition {
  id: HardCostCategoryId;
  label: string;
}

export const HARD_COST_CATEGORIES: HardCostCategoryDefinition[] = [
  { id: "license", label: "License" },
  { id: "hosting", label: "Hosting" },
  { id: "third_party", label: "Third Party" },
  { id: "travel", label: "Travel" },
  { id: "equipment", label: "Equipment" },
  { id: "other", label: "Other" },
];

export interface StaffLineItem {
  role: OfferingRoleId;
  hours: number;
  rate?: number; // Optional override; uses OFFERING_ROLES default if omitted
}

export interface HardCostLineItem {
  description: HardCostCategoryId;
  amount: number;
}

/** Pricing model for product offerings */
export type PricingModel = "fixed_recurring" | "one_time" | "variable_recurring" | "project_based";

export type Offering = {
  id: string;
  website_id: string | null;
  title: string;
  description: string | null;
  executive_summary: string | null;
  tags: string[];
  delivery_kit: string | null;
  one_pager_pdf_url: string | null;
  /** Public web-optimized offering image URL */
  image_url: string | null;
  /** CTA URL (e.g. https://chatbot.westtn.consulting) */
  cta_href: string | null;
  /** CTA button text (e.g. Sign Up, Get Quote) */
  cta_text: string | null;
  /** Pricing model type */
  pricing_model: PricingModel | null;
  /** Base price (monthly for recurring, one-time amount) */
  base_price: number | null;
  /** Human-readable price display (e.g. $79/mo, Variable) */
  pricing_display: string | null;
  /** Project slug from projects-data for linkage */
  linked_project_slug: string | null;
  staff_line_items: StaffLineItem[];
  hard_cost_line_items: HardCostLineItem[];
  total_hours: number;
  total_hard_costs: number;
  total_cost: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
};

export interface CreateOfferingInput {
  website_id?: string;
  title: string;
  description?: string;
  cta_href?: string;
  cta_text?: string;
  pricing_model?: PricingModel;
  base_price?: number;
  pricing_display?: string;
  linked_project_slug?: string;
  executive_summary?: string;
  tags?: string[];
  delivery_kit?: string;
  one_pager_pdf_url?: string;
  image_url?: string;
  staff_line_items?: StaffLineItem[];
  hard_cost_line_items?: HardCostLineItem[];
}

export interface UpdateOfferingInput {
  website_id?: string;
  title?: string;
  cta_href?: string;
  cta_text?: string;
  pricing_model?: PricingModel;
  base_price?: number;
  pricing_display?: string;
  linked_project_slug?: string;
  description?: string;
  executive_summary?: string;
  tags?: string[];
  delivery_kit?: string;
  one_pager_pdf_url?: string;
  image_url?: string;
  staff_line_items?: StaffLineItem[];
  hard_cost_line_items?: HardCostLineItem[];
}
